import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * Set ``document.title`` to a localised page title (WCAG 2.4.2 Page
 * Titled). Pages call ``usePageTitle("home")`` and we resolve the
 * i18n key under ``pageTitle.<key>`` and append the brand product name
 * when one is available.
 *
 * We pull the brand product name out of the existing ``<title>`` set
 * by ``BrandFavicon`` (which runs first and writes the brand name as
 * the document title). Reading from the live DOM keeps this hook
 * decoupled from the brand cascade - pages don't need to thread the
 * brand object through to set their title.
 */
export function usePageTitle(key: string): void {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    const page = t(`pageTitle.${key}`);
    // ``BrandFavicon`` snapshots the previous title before mutating to
    // the brand product name, so by the time a page mounts the
    // <title> element holds the brand name. Falling back to a literal
    // keeps the title sane even if BrandFavicon hasn't run yet (login
    // page, anchor lookup error, etc.).
    const brandName = readBrandName();
    document.title = brandName ? `${page} · ${brandName}` : page;
  }, [key, t, i18n.language]);
}

function readBrandName(): string | null {
  if (typeof document === "undefined") return null;
  const current = document.title.trim();
  if (!current) return null;
  // If a previous page already set "Foo · Brand", strip the leading
  // page name so we don't accumulate a chain across navigations.
  const sep = current.lastIndexOf(" · ");
  if (sep > 0) return current.slice(sep + 3).trim();
  return current;
}
